import { useState, useEffect, useRef } from 'react';
import { NoRecords, PageLoader } from '../../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import { ReferralTransaction } from '../api/apiReferral';
import { ArrowUp, ArrowDown, Copy, Check, ExternalLink, Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { copyToClipboard } from '../../../lib/copytoClipboard';
import { useModalContext } from '../../../contexts/ModalContext';
import { useToast } from '../../../hooks/usetoast';
import { useCheckAdminAccess } from '../../WhiteIp/api/whiteipapi';
import { logout } from '../../../hooks/logout';
import PermissionDenied from '../../common/permissionDenied';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { transactionurl } from "../../../lib/transactionurl";
import { useBreadcrumbPagination } from '../../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../../utils/BreadcrumbPagination';
import { useDebounce } from '../../../hooks/debounce';
import { displayValue } from '../../../hooks/export';



function TotalReferralhistory() {

    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [double, setDouble] = useState(false);
    const [search, setSearch] = useState("");
    const searchText = useDebounce(search);
    const { theme } = useModalContext()
    const toast = useToast();
    const [sortOrder, setSortOrder] = useState("desc");
    const { id } = useParams();
    const [userEmail, setUserEmail] = useState(false);
    const [referralEmail, setReferralEmail] = useState(false);

    const { data: getReferralHistory, isLoading } = ReferralTransaction({
        id,
        page: searchText ? 0 : tempPage,
        size: tempRows,
        searchText,
        sortField: "createdAt",
        sortOrder
    });

    const totalRecords = getReferralHistory?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });

    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);

    const copyUserEmailAddress = async data => {
        setUserEmail(data._id)
        await copyToClipboard(data?.userId?.email)
        toast.success("Copied")
        setTimeout(() => {
            setUserEmail()
        }, 800)
    }

    const copyReferralEmailAddress = async data => {
        setReferralEmail(data._id)
        await copyToClipboard(data?.referUserId?.email)
        toast.success("Copied")
        setTimeout(() => {
            setReferralEmail()
        }, 800)
    }

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();

    }, []);

    const renderCopyIcon = (isCopied, onClick, theme) => (
        isCopied ?
            <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : "black" }} />
            :
            <Copy onClick={onClick} size={20} className="ml-5" style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : "black" }} />
    );

    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'Referral Management' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };


    const copytxnhashAddress = async data => {
        setDouble(data._id)
        await copyToClipboard(data)
        toast.success("Copied")
        setTimeout(() => {
            setDouble()
        }, 800)
    }


    const renderTransactionLink = (hash, urlPrefix, isCopied, onClickCopy, theme) => (
        hash ? (
            <>
                {`${hash.slice(0, 3)}...${hash.slice(-3)}`}
                {renderCopyIcon(isCopied, onClickCopy, theme)}
                <a href={urlPrefix + hash} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <ExternalLink style={{ marginLeft: "5px" }} size={20} />
                </a>
            </>
        ) : "-"
    );


    const renderTotalReferralhistoryTable = () => (
        getReferralHistory?.data?.usersHistory?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="Referral transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">User Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">Referrer Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">From User Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">To User Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">Reward Amount</TableCell>
                                    <TableCell className='text-nowrap' align="center">Reward Status</TableCell>

                                    <TableCell className='text-nowrap' align="center">
                                        Date & Time{' '}
                                        {sortOrder !== 'desc' ? (
                                            <ArrowUp
                                                onClick={() => setSortOrder('desc')}
                                                style={{ cursor: 'pointer' }}
                                                aria-label="Sort descending"
                                            />
                                        ) : (
                                            <ArrowDown
                                                onClick={() => setSortOrder('asc')}
                                                style={{ cursor: 'pointer' }}
                                                aria-label="Sort ascending"
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getReferralHistory?.data?.usersHistory?.map((row, i) => (
                                    <TableRow key={row?._id || i}>
                                        <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">
                                            {row?.referUserId?.email || "-"}
                                            {referralEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyReferralEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.userId?.email || "admin@roupay.com"}
                                            {userEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyUserEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>



                                        <TableCell align="center">
                                            {renderTransactionLink(row.fromTxnHash, transactionurl["POL"], double === row._id, () => copytxnhashAddress(row.fromTxnHash), theme)}
                                        </TableCell>


                                        <TableCell align="center">
                                            {renderTransactionLink(row.toTxnHash, transactionurl["POL"], double === row._id, () => copytxnhashAddress(row.toTxnHash), theme)}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {row?.rewardAmount ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                    {displayValue(row.rewardAmount, 6)}
                                                    {row?.currencyImage && (
                                                        <img
                                                            src={row.currencyImage}
                                                            alt="Currency Icon"
                                                            style={{
                                                                height: '20px',
                                                                width: '20px',
                                                                borderRadius: '50%',
                                                                verticalAlign: 'middle',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        {
                                            row?.rewardStatus == "Pending" ?
                                                <TableCell align="center" className=' text-warning'>{row?.rewardStatus || "-"}</TableCell>
                                                :
                                                <TableCell align="center" className=' text-success'>{row?.rewardStatus || "-"}</TableCell>


                                        }
                                        <TableCell align="center">
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <section>
                <h4>Referral Reward History</h4>
                <div className="ad_pg_bx bg-white p-3">
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-icon">
                                    <Search size={20} color="#aaa9a9" />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    placeholder="Search Email"
                                    value={search}
                                />
                            </div>
                        </div>
                    </div>
                    {isLoading ? <PageLoader /> : renderTotalReferralhistoryTable()}
                </div>
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default TotalReferralhistory;